import { HourMinute } from "./time"

import { getFragmentData } from "@/main/gql/__generated__/fragment-masking"
import { FragmentType } from "@/public/gql/__generated__"
import { graphql, DocumentType } from "@/public/gql/__generated__/gql"
import { addDate, dayEquals } from "@/utils/date"

const dateReservableSlotsFragment = graphql(/* GraphQL */ `
	fragment dateReservableSlots on DateReservableSlots {
		date
		reservableStartHour
		reservableStartMinute
		reservableEndHour
		reservableEndMinute
		reservableSlotsWithStaffChunks {
			staff {
				id
				name
			}
			reservableSlots {
				startHour
				startMinute
				durationMinute
			}
		}
		reservableSlotsWithoutStaff {
			startHour
			startMinute
			durationMinute
		}
	}
`)

type ReservableSlot = DocumentType<
	typeof dateReservableSlotsFragment
>["reservableSlotsWithStaffChunks"][number]["reservableSlots"]

export class SevenDaysReservableSlotsChunks {
	sevenDaysChunks: {
		date: Date
		slots?: DocumentType<typeof dateReservableSlotsFragment>
	}[]
	allChunks: DocumentType<typeof dateReservableSlotsFragment>[]

	private updateFields(
		startDay: Date,
		chunks: DocumentType<typeof dateReservableSlotsFragment>[]
	) {
		const getSlots = (date: Date) => {
			return chunks.find((chunk) => {
				const chunkDate = new Date(chunk.date)
				return dayEquals(date, chunkDate)
			})
		}

		this.sevenDaysChunks = [
			{ date: startDay, slots: getSlots(startDay) },
			{ date: addDate(startDay, 1), slots: getSlots(addDate(startDay, 1)) },
			{ date: addDate(startDay, 2), slots: getSlots(addDate(startDay, 2)) },
			{ date: addDate(startDay, 3), slots: getSlots(addDate(startDay, 3)) },
			{ date: addDate(startDay, 4), slots: getSlots(addDate(startDay, 4)) },
			{ date: addDate(startDay, 5), slots: getSlots(addDate(startDay, 5)) },
			{ date: addDate(startDay, 6), slots: getSlots(addDate(startDay, 6)) },
		]
	}

	constructor(
		startDay: Date,
		chunks: FragmentType<typeof dateReservableSlotsFragment>[]
	) {
		this.allChunks = chunks.map((chunk) => {
			return getFragmentData(dateReservableSlotsFragment, chunk)
		})
		this.sevenDaysChunks = []
		this.updateFields(startDay, this.allChunks)
	}

	private getMinStartTime(): HourMinute {
		let minStartTime = new HourMinute(11, 0) // 遅くても9時から表示
		this.allChunks.forEach((slots) => {
			const hourMinute = new HourMinute(
				slots.reservableStartHour,
				slots.reservableStartMinute
			)
			if (hourMinute.less(minStartTime)) {
				minStartTime = hourMinute
			}
		})
		return minStartTime
	}

	private getMaxStartTime(): HourMinute {
		let maxStartTime = new HourMinute(20, 0) // 早くても20時まで表示
		this.allChunks.forEach((slots) => {
			if (!slots) {
				return
			}

			const hourMinute = new HourMinute(
				slots.reservableEndHour,
				slots.reservableEndMinute
			)
			if (maxStartTime.less(hourMinute)) {
				maxStartTime = hourMinute
			}
		})
		return maxStartTime.addMinute(-30) // (max reservableEnd time) - 30
	}

	getStartTimes(): HourMinute[] {
		const minStartTime = this.getMinStartTime()
		const maxStartTime = this.getMaxStartTime()

		const startTimes: HourMinute[] = []
		for (
			let hourMinute = minStartTime;
			hourMinute.less(maxStartTime);
			hourMinute = hourMinute.addMinute(30)
		) {
			startTimes.push(hourMinute)
		}
		startTimes.push(maxStartTime)

		return startTimes
	}

	getSlotsByDateAndStaff(date: Date, staffID: string | null): ReservableSlot {
		const slots = this.sevenDaysChunks.find((chunk) =>
			dayEquals(chunk.date, date)
		)?.slots
		if (!slots) {
			return []
		}

		if (!staffID) {
			return slots.reservableSlotsWithoutStaff
		}

		const slotsWithStaffChunks = slots.reservableSlotsWithStaffChunks
		const slotsWithStaff = slotsWithStaffChunks.find((chunk) => {
			return chunk.staff?.id === staffID
		})

		return slotsWithStaff?.reservableSlots ?? []
	}

	// なぜかうごかない...
	// equals(hourMinuter: HourMinute) {
	//   return this.hour === hourMinute.hour && this.minute === hourMinute.minute;
	// }
}
